import { AxiosRequestConfig, AxiosResponse } from 'axios'

interface FailedRequest {
  id: string
  config: AxiosRequestConfig
  timestamp: number
  retryCount: number
  maxRetries: number
}

interface RetryResult {
  success: boolean
  response?: AxiosResponse
  error?: any
}

class NetworkRetryManager {
  private failedRequests: Map<string, FailedRequest> = new Map()

  private readonly MAX_RETRIES = 3

  private readonly MAX_RETRY_AGE = 5 * 60 * 1000 // 5 minutes

  private isRetrying = false

  /**
   * Generates a unique ID for a request based on its config
   */
  private static generateRequestId(config: AxiosRequestConfig): string {
    const url = config.url || 'unknown'
    const method = config.method || 'GET'
    const data = config.data
      ? JSON.stringify(config.data).substring(0, 100)
      : ''
    return `${method}-${url}-${btoa(data).substring(0, 10)}-${Date.now()}`
  }

  /**
   * Determines if a request should be retried based on the error
   */
  private static shouldRetryRequest(error: any): boolean {
    if (!error) return false

    const message = error.message?.toLowerCase() || ''
    const isNetworkError =
      message.includes('network error') ||
      message.includes('failed to fetch') ||
      message.includes('timeout') ||
      message.includes('connection') ||
      error.code === 'NETWORK_ERROR' ||
      !error.response // No response usually means network issue

    // Don't retry auth-related errors or client errors
    const status = error.response?.status
    if (status && status >= 400 && status < 500 && status !== 408) {
      return false
    }

    return isNetworkError
  }

  /**
   * Store a failed request for later retry
   */
  storeFailedRequest(config: AxiosRequestConfig, error: any): void {
    if (!NetworkRetryManager.shouldRetryRequest(error)) {
      return
    }

    const id = NetworkRetryManager.generateRequestId(config)
    const existingRequest = this.failedRequests.get(id)

    // If we already have this request and it has reached max retries, don't store it again
    if (existingRequest && existingRequest.retryCount >= this.MAX_RETRIES) {
      return
    }

    const failedRequest: FailedRequest = {
      id,
      config: { ...config },
      timestamp: Date.now(),
      retryCount: existingRequest?.retryCount || 0,
      maxRetries: this.MAX_RETRIES,
    }

    this.failedRequests.set(id, failedRequest)
    console.log(
      `Stored failed request for retry: ${config.method?.toUpperCase()} ${
        config.url
      }`
    )

    // Clean up old requests
    this.cleanupOldRequests()
  }

  /**
   * Remove old failed requests that are too old to retry
   */
  private cleanupOldRequests(): void {
    const now = Date.now()
    const keysToDelete: string[] = []

    this.failedRequests.forEach((request, key) => {
      if (now - request.timestamp > this.MAX_RETRY_AGE) {
        keysToDelete.push(key)
      }
    })

    keysToDelete.forEach((key) => {
      this.failedRequests.delete(key)
    })

    if (keysToDelete.length > 0) {
      console.log(`Cleaned up ${keysToDelete.length} old failed requests`)
    }
  }

  /**
   * Retry a single failed request
   */
  private static async retryRequest(
    failedRequest: FailedRequest
  ): Promise<RetryResult> {
    try {
      const axios = (await import('axios')).default

      // Update retry count
      failedRequest.retryCount += 1

      console.log(
        `Retrying request (attempt ${failedRequest.retryCount}/${
          failedRequest.maxRetries
        }): ${failedRequest.config.method?.toUpperCase()} ${
          failedRequest.config.url
        }`
      )

      // Create a clean config for retry - the URL already includes base URL from original request
      const retryConfig = {
        ...failedRequest.config,
      }

      // Create a fresh axios instance without the URL-modifying interceptors
      const freshAxios = axios.create({
        withCredentials: true,
        timeout: 30000, // 30 second timeout for retries
      })

      // Ensure we have all necessary headers from the original request
      retryConfig.headers = {
        ...retryConfig.headers,
        'Cache-Control':
          'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
        Pragma: 'no-cache',
        Expires: '0',
      }

      // Copy authorization header if it exists
      if (failedRequest.config.headers?.Authorization) {
        retryConfig.headers.Authorization =
          failedRequest.config.headers.Authorization
      }

      const response = await freshAxios.request(retryConfig)

      console.log(
        `✅ Request retry successful: ${failedRequest.config.method?.toUpperCase()} ${
          failedRequest.config.url
        }`
      )

      // Handle the successful response to update Redux state and clear loading states
      try {
        const { apiResponseManager } = await import('./apiResponseHandler')
        await apiResponseManager.handleSuccessfulRetry(
          response,
          failedRequest.config
        )
      } catch (error) {
        console.warn('Error handling successful retry response:', error)
      }

      return {
        success: true,
        response,
      }
    } catch (error) {
      console.log(
        `❌ Request retry failed: ${failedRequest.config.method?.toUpperCase()} ${
          failedRequest.config.url
        }`,
        error
      )

      return {
        success: false,
        error,
      }
    }
  }

  /**
   * Process a single batch of requests
   */
  private async processBatch(
    batch: FailedRequest[],
    results: { success: number; failed: number; removed: number }
  ): Promise<void> {
    const batchPromises = batch.map(async (failedRequest) => {
      const result = await NetworkRetryManager.retryRequest(failedRequest)

      if (result.success) {
        // Remove successful requests
        this.failedRequests.delete(failedRequest.id)
        results.success += 1
      } else if (failedRequest.retryCount >= this.MAX_RETRIES) {
        // Remove requests that have exceeded max retries
        this.failedRequests.delete(failedRequest.id)
        results.removed += 1
      } else {
        // Update the stored request with new retry count
        this.failedRequests.set(failedRequest.id, failedRequest)
        results.failed += 1
      }
    })

    await Promise.all(batchPromises)
  }

  /**
   * Process all batches recursively to avoid await-in-loop
   */
  private async processBatchesRecursively(
    batches: FailedRequest[][],
    batchIndex: number,
    results: { success: number; failed: number; removed: number }
  ): Promise<void> {
    if (batchIndex >= batches.length) {
      return
    }

    const batch = batches[batchIndex]
    await this.processBatch(batch, results)

    // Add delay between batches (except for the last batch)
    if (batchIndex < batches.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 500))
    }

    // Process next batch recursively
    await this.processBatchesRecursively(batches, batchIndex + 1, results)
  }

  /**
   * Retry all failed requests when network recovers
   */
  async retryFailedRequests(): Promise<void> {
    console.log(
      `🔄 retryFailedRequests called. Currently retrying: ${this.isRetrying}, Pending: ${this.failedRequests.size}`
    )

    if (this.isRetrying) {
      console.log('⏳ Already retrying, skipping...')
      return
    }

    if (this.failedRequests.size === 0) {
      console.log('✅ No failed requests to retry')
      return
    }

    this.isRetrying = true
    console.log(
      `🔄 Starting to retry ${this.failedRequests.size} failed requests...`
    )

    const requestsToRetry = Array.from(this.failedRequests.values())
    const results: { success: number; failed: number; removed: number } = {
      success: 0,
      failed: 0,
      removed: 0,
    }

    // Create batches to avoid overwhelming the server
    const batchSize = 3
    const batches: FailedRequest[][] = []
    for (let i = 0; i < requestsToRetry.length; i += batchSize) {
      batches.push(requestsToRetry.slice(i, i + batchSize))
    }

    // Process all batches recursively to avoid await-in-loop
    await this.processBatchesRecursively(batches, 0, results)

    console.log(
      `🏁 Retry completed: ${results.success} successful, ${results.failed} failed, ${results.removed} removed`
    )

    // Clear any remaining loading states as a fallback
    if (results.success > 0) {
      try {
        const { ApiResponseManager } = await import('./apiResponseHandler')
        await ApiResponseManager.clearAllLoadingStates()
      } catch (error) {
        console.warn('Error clearing loading states:', error)
      }
    }

    // Don't show success message here to avoid duplicate toasts
    // The success message is already shown in errorToastManager.handleNetworkRecovery()
    console.log(
      `✅ Successfully retried ${results.success} request${
        results.success > 1 ? 's' : ''
      }`
    )

    this.isRetrying = false
  }

  /**
   * Show success message without circular dependency
   */
  private static showSuccessMessage(successCount: number): void {
    // Use a simple console log to avoid any circular dependencies
    console.log(
      `✅ Successfully retried ${successCount} failed request${
        successCount > 1 ? 's' : ''
      }`
    )

    // Show browser notification if available
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('Network Restored', {
        body: `Successfully retried ${successCount} failed request${
          successCount > 1 ? 's' : ''
        }`,
        icon: '/favicon.ico',
      })

      // Auto-close notification after 3 seconds
      setTimeout(() => {
        notification.close()
      }, 3000)
    }
  }

  /**
   * Get count of pending failed requests
   */
  getPendingRequestsCount(): number {
    return this.failedRequests.size
  }

  /**
   * Get list of pending failed requests (for debugging)
   */
  getPendingRequests(): FailedRequest[] {
    return Array.from(this.failedRequests.values())
  }

  /**
   * Clear all failed requests (useful for testing or manual reset)
   */
  clearFailedRequests(): void {
    const count = this.failedRequests.size
    this.failedRequests.clear()
    console.log(`Cleared ${count} failed requests`)
  }

  /**
   * Check if currently retrying requests
   */
  isCurrentlyRetrying(): boolean {
    return this.isRetrying
  }
}

// Create and export singleton instance
export const networkRetryManager = new NetworkRetryManager()

// Export the class for testing purposes
export { NetworkRetryManager }
